/**
 * renderers.js — ACTION vs CHAT response rendering for VukzAssistPanel.
 *
 * Handles two response modes from the VUKZ Assist agent:
 *   - CHAT: Markdown content → rendered as formatted text
 *   - ACTION: Structured JSON → navigate, toast, show link, etc.
 */
import { surfaceToRoute } from './navigationSurfaces';

/**
 * Execute an ACTION response.
 *
 * @param {Object} response  — { type: 'ACTION', action: string, payload: object }
 * @param {Function} navigate — react-router navigate function
 * @param {Function} [onToast] — optional toast callback (message, variant)
 * @returns {{ handled: boolean, label?: string }}
 */
export const executeAction = (response, navigate, onToast) => {
  if (!response || response.type !== 'ACTION') {
    return { handled: false };
  }

  const { action, payload = {} } = response;

  switch (action) {
    case 'navigate':
      if (payload.route) {
        navigate(payload.route);
        return { handled: true, label: `Navegando para ${payload.route}` };
      }
      return { handled: false };

    case 'toast':
      if (onToast) {
        onToast(payload.message || 'Ação concluída', payload.variant || 'success');
      }
      return { handled: true, label: payload.message };

    case 'created_session':
      if (onToast) {
        onToast(
          `Sessão "${payload.title || ''}" criada (ID: ${payload.session_id})`,
          'success'
        );
      }
      if (payload.session_id && navigate) {
        // Navigate to sessions board after short delay.
        // Consolidação ago/2026: era /coach/training/sessions/{id}, mas página
        // de detalhe de sessão NUNCA existiu — o path com id caía no wildcard
        // e abria a tab default. Descartar o session_id aqui é DELIBERADO:
        // nada se perde (o board é o único destino real). Deep link com id
        // vira melhoria futura se a página de detalhe nascer.
        setTimeout(() => {
          navigate('/treinos?tab=sessions');
        }, 1500);
      }
      return {
        handled: true,
        label: `✅ Sessão criada: ${payload.title || payload.session_id}`,
      };

    case 'created_plan':
      if (onToast) {
        onToast(
          `Plano "${payload.title || ''}" criado (ID: ${payload.plan_id})`,
          'success'
        );
      }
      return {
        handled: true,
        label: `✅ Plano criado: ${payload.title || payload.plan_id}`,
      };

    // ── FILL_FORM v1 (25/08/2026) ──
    // O agente sugere um patch para o formulário aberto. NADA é aplicado
    // aqui: devolvemos o patch para o panel renderizar o card de
    // confirmação. LLM sugere → domínio valida → usuário confirma → API
    // canônica salva.
    case 'fill_form': {
      if (!payload.patch || typeof payload.patch !== 'object') {
        return { handled: false };
      }
      // Identidade NUNCA entra via patch (gate do builder). Quando o target
      // já foi aplicado pelo caminho canônico (applySelection), reenviar
      // athleteId/squadId só geraria "Recusado pelo formulário" — removemos
      // defensivamente ANTES da bolha (o gate do domínio continua ativo).
      const cleanPatch = { ...payload.patch };
      delete cleanPatch.athleteId;
      delete cleanPatch.squadId;
      delete cleanPatch.targetType;
      // Guarda anti-destrutiva (28/08): blocks:[] NÃO significa "sem
      // alterações" — aplicado, apagaria o treino. Limpar será sempre
      // uma operação explícita (futuro clear_blocks), nunca implícita.
      if (Array.isArray(cleanPatch.blocks) && cleanPatch.blocks.length === 0) {
        delete cleanPatch.blocks;
      }
      if (Object.keys(cleanPatch).length === 0) {
        return { handled: false };
      }
      return {
        handled: true,
        label: 'Sugestão de preenchimento pronta para revisão',
        fillForm: {
          target: payload.target || '',
          schemaVersion: payload.schema_version || 'v1',
          patch: cleanPatch,
          missingFields: payload.missing_fields || [],
          warnings: payload.warnings || [],
          requiresConfirmation: payload.requires_confirmation !== false,
          // Continuidade (27/08): fill_form intermediário NÃO encerra a
          // conversa — só o final (READY_FOR_REVIEW) mostra "revise e salve".
          final: payload.final === true,
        },
        // Workflow de estrutura (27/08): o backend emenda a PRÓXIMA pergunta
        // (chips) na mesma resposta — a conversa nunca fica sem próximo passo.
        selection: payload.followup && Array.isArray(payload.followup.options)
          && payload.followup.options.length > 0
          ? {
              field: payload.followup.field,
              question: payload.followup.question || 'Escolha uma opção',
              options: payload.followup.options,
              groups: null,
              context: payload.followup.context || {},
              selectionMode: 'single',
              min: 1,
              max: 1,
              warnings: [],
            }
          : undefined,
      };
    }

    // ── REQUEST_SELECTION (Single Assist, 26/08/2026) ──
    // Workflow CREATE_TRAINING: o backend responde deterministicamente
    // com opções REAIS (IDs do banco — nunca do modelo). O panel renderiza
    // chips; ao clicar, a página aplica a seleção via ponte confiável
    // (applySelection do formulário) — NUNCA via patch do LLM.
    case 'request_selection':
      if (!payload.field || !Array.isArray(payload.options) || payload.options.length === 0) {
        return { handled: false };
      }
      return {
        handled: true,
        label: payload.question || 'Escolha uma opção',
        selection: {
          field: payload.field,
          question: payload.question || 'Escolha uma opção',
          options: payload.options,
          // Pipeline TARGET (27/08): grupos Atletas/Subgrupos — UI agrupada
          groups: Array.isArray(payload.groups) ? payload.groups : null,
          context: payload.context || {},
          // multi-select (SETUP_CLUB, 26/08/2026): backend decide o modo.
          selectionMode: payload.selection_mode === 'multiple' ? 'multiple' : 'single',
          min: Number.isFinite(payload.min) ? payload.min : 1,
          max: Number.isFinite(payload.max) ? payload.max : 1,
          // quota do plano etc. — aviso vem do BACKEND (entitlements)
          warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
        },
      };

    // ── APPLY_SELECTION (pipeline TARGET, 27/08/2026) ──
    // O usuário respondeu o slot em TEXTO LIVRE ("Lucas Ferreira") e o
    // WORKFLOW resolveu server-side contra o conjunto autorizado — mesmo
    // resultado do clique no chip, zero LLM para resolver identidade.
    case 'apply_selection':
      if (!payload.field || !payload.option?.id) {
        return { handled: false };
      }
      return {
        handled: true,
        label: `Selecionado: ${payload.option.label || payload.option.id}`,
        autoSelection: {
          field: payload.field,
          option: payload.option,
          context: payload.context || {},
        },
      };

    case 'link':
      if (payload.url) {
        window.open(payload.url, '_blank', 'noopener,noreferrer');
        return { handled: true, label: `Abrindo: ${payload.label || payload.url}` };
      }
      return { handled: false };

    // ── Execution Canvas actions (from Foundry agent) ──

    case 'created_execution':
      if (onToast) {
        onToast(`Execução iniciada para plano ${payload.plan_id}`, 'success');
      }
      if (payload.plan_id && navigate) {
        const route = payload.mode === 'coach'
          ? `/coach/training/execute/${payload.plan_id}`
          : `/athlete/training/execute/${payload.plan_id}`;
        setTimeout(() => navigate(route), 800);
      }
      return {
        handled: true,
        label: `▶️ Execução iniciada: ${payload.plan_title || payload.plan_id}`,
      };

    case 'mark_item_done':
      // Dispatched as a DOM event for Canvas to listen
      if (payload.execution_id && payload.item_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:item', {
          detail: { execution_id: payload.execution_id, item_id: payload.item_id, is_done: true },
        }));
      }
      return {
        handled: true,
        label: `✅ Item marcado: ${payload.label || payload.item_id}`,
      };

    case 'add_execution_note':
      if (payload.execution_id && payload.item_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:item', {
          detail: { execution_id: payload.execution_id, item_id: payload.item_id, notes: payload.notes },
        }));
      }
      return {
        handled: true,
        label: `📝 Nota adicionada: ${(payload.notes || '').substring(0, 40)}...`,
      };

    case 'set_execution_metric':
      if (payload.execution_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:header', {
          detail: { execution_id: payload.execution_id, field: payload.field, value: payload.value },
        }));
      }
      return {
        handled: true,
        label: `📊 ${payload.field}: ${payload.value}`,
      };

    case 'complete_execution':
      if (payload.execution_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:complete', {
          detail: { execution_id: payload.execution_id, ...payload },
        }));
      }
      if (onToast) {
        onToast('Treino concluído com sucesso!', 'success');
      }
      return {
        handled: true,
        label: '🏁 Treino concluído!',
      };

    default:
      console.warn('[renderers] Unknown ACTION:', action, payload);
      return { handled: false };
  }
};

/* ════════════════════════════════════════════════════════
   ASSIST v2 (C2a+b, 29/08/2026) — message + actions[]

   Filosofia: ação COMPLEMENTA a fala, nunca a substitui.
   Split obrigatório (aprovado no GO do C2):
     - describeAction  → PURO. Decide componente/label/CTA. Zero efeitos.
     - runImmediateEffect → SOMENTE allowlist (ecos de execução já
       persistidos pela tool). navigate/link/created_execution NUNCA
       executam automaticamente no v2 — viram CTA (botão).
   O LLM não é autoridade sobre navegação: qualquer navigate/link que
   ele emita vira botão. Auto-navegação exigiria prova de pedido
   explícito controlada pelo SISTEMA — não existe no C2, logo: botão.
   ════════════════════════════════════════════════════════ */

const _noopNavigate = () => {};
const _noopToast = () => {};

/* ════════════════════════════════════════════════════════
   NAV-2 (01/09/2026) — Navigation Execution Policy

   INVARIANTE DE PRODUTO (não propriedade acidental do envelope v2):
   Coach (TREINADOR) + action de navegação → CTA por default,
   independentemente de ASSIST_RESPONSE_V2_COACH. Desligar a flag v2
   NÃO reativa auto-navigation legada.

   Futuro C4d-3: 'auto' só entrará quando o BACKEND enviar metadata
   validada explícita (ex.: navigation_execution="auto", composta por
   guard NAV-1 explicit_ui + referência autorizada C4d-2). A action
   navigate legada NUNCA será bypass — ausência da autorização = CTA.

   Outros roles: mantêm o comportamento v1 encontrado (auto) até terem
   seu próprio gate — mudança global cega seria regressão silenciosa.
   ════════════════════════════════════════════════════════ */

/** Actions que movem a interface (router ou nova aba). */
export const NAVIGATION_ACTIONS = new Set(['navigate', 'link', 'created_execution']);

/**
 * Decide como uma action de navegação deve ser executada.
 * PURA — zero side effects.
 * @param {{role?: string, responseVersion?: 'v1'|'v2', action: string}} p
 * @returns {'cta'|'auto'|'n/a'}
 */
export const navigationExecutionPolicy = ({ role, responseVersion, action } = {}) => {
  if (!NAVIGATION_ACTIONS.has(action)) return 'n/a';
  // v2: CTA para todos (contrato C2 preservado).
  if (responseVersion === 'v2') return 'cta';
  // Coach: CTA-safe em QUALQUER versão de envelope (invariante NAV-2).
  if ((role || '').toUpperCase() === 'TREINADOR') return 'cta';
  // Legado dos demais roles — documentado, não alterado sem gate próprio.
  return 'auto';
};

/**
 * Describe an ASSIST v2 action — PURE, no side effects.
 * @param {{action: string, payload?: object}} item
 * @returns {{ kind: 'fillForm'|'selection'|'autoSelection'|'cta'|'echo'|'ignored',
 *            label?: string, cta?: {label: string, route?: string, url?: string},
 *            fillForm?: object, selection?: object, autoSelection?: object }}
 */
export const describeAction = (item) => {
  if (!item || typeof item.action !== 'string') return { kind: 'ignored' };
  const { action, payload = {} } = item;

  switch (action) {
    // Casos já puros no executeAction v1 — delega (mesma validação/limpeza
    // de patch, mesmos gates). Reuso deliberado: um só lugar para as regras.
    case 'fill_form':
    case 'request_selection':
    case 'apply_selection': {
      const r = executeAction({ type: 'ACTION', action, payload }, _noopNavigate, _noopToast);
      if (!r.handled) return { kind: 'ignored' };
      if (r.fillForm) return { kind: 'fillForm', label: r.label, fillForm: r.fillForm, selection: r.selection };
      if (r.selection) return { kind: 'selection', label: r.label, selection: r.selection };
      if (r.autoSelection) return { kind: 'autoSelection', label: r.label, autoSelection: r.autoSelection };
      return { kind: 'ignored' };
    }

    // Navegação (C4d-3, 02/09/2026 — endurecido pós PROD FAIL): a ÚNICA
    // fonte executável é o destino semântico validado pelo backend
    // ({surface, params} → surfaceToRoute). mode=auto + surface mapeável →
    // autoNavigate (1x, o painel executa); cta → CTA com o MESMO destino
    // validado; mode=reject ou surface ausente/inválida → zero navegação e
    // zero CTA (raw payload.route NUNCA vira link — foi exatamente o buraco
    // do /staff/training-builder alucinado pelo LLM).
    case 'navigate': {
      const execMode = item.navigation_execution?.mode;
      if (execMode === 'reject') return { kind: 'ignored' };
      const route = payload.surface
        ? surfaceToRoute(payload.surface, payload.params || {})
        : null;
      if (!route) return { kind: 'ignored' };
      const label = payload.label || `Ir para ${payload.surface}`;
      if (execMode === 'auto') {
        return { kind: 'autoNavigate', label, route };
      }
      return { kind: 'cta', cta: { label, route } };
    }

    case 'link':
      if (!payload.url) return { kind: 'ignored' };
      return { kind: 'cta', cta: { label: payload.label || 'Abrir link', url: payload.url } };

    case 'created_execution': {
      if (!payload.plan_id) return { kind: 'ignored' };
      const route = payload.mode === 'coach'
        ? `/coach/training/execute/${payload.plan_id}`
        : `/athlete/training/execute/${payload.plan_id}`;
      return {
        kind: 'cta',
        label: `▶️ Execução iniciada: ${payload.plan_title || payload.plan_id}`,
        cta: { label: 'Abrir execução', route },
      };
    }

    // Ecos de execução (write já feito pela tool) — efeito via allowlist.
    case 'mark_item_done':
      return { kind: 'echo', label: `✅ Item marcado: ${payload.label || payload.item_id}` };
    case 'add_execution_note':
      return { kind: 'echo', label: `📝 Nota adicionada: ${(payload.notes || '').substring(0, 40)}...` };
    case 'set_execution_metric':
      return { kind: 'echo', label: `📊 ${payload.field}: ${payload.value}` };
    case 'complete_execution':
      return { kind: 'echo', label: '🏁 Treino concluído!' };

    case 'toast':
      return { kind: 'echo', label: payload.message || 'Ação concluída' };

    // Legado v1 mantido por compat (outros roles) — no v2 do Coach o
    // prompt não emite mais; se chegar, vira eco inofensivo.
    case 'created_session':
      return { kind: 'echo', label: `✅ Sessão criada: ${payload.title || payload.session_id}` };
    case 'created_plan':
      return { kind: 'echo', label: `✅ Plano criado: ${payload.title || payload.plan_id}` };

    default:
      // Fail-soft: action desconhecida não derruba o turno — a fala é
      // o elemento principal. Telemetria via console (C6: telemetria real).
      console.warn('[renderers] Unknown ASSIST action (ignored):', action, payload);
      return { kind: 'ignored' };
  }
};

/** Allowlist de efeitos imediatos no v2 — ecos de execução apenas. */
const IMMEDIATE_EFFECT_ACTIONS = new Set([
  'mark_item_done', 'add_execution_note', 'set_execution_metric', 'complete_execution',
]);

/**
 * Run the immediate side effect of an ASSIST v2 action, if allowlisted.
 * navigate/link/created_execution are NEVER here (CTA only).
 * @param {{action: string, payload?: object}} item
 * @param {{onToast?: Function}} [handlers]
 * @returns {boolean} whether an effect ran
 */
export const runImmediateEffect = (item, { onToast } = {}) => {
  if (!item || !IMMEDIATE_EFFECT_ACTIONS.has(item.action)) return false;
  const { action, payload = {} } = item;

  switch (action) {
    case 'mark_item_done':
      if (payload.execution_id && payload.item_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:item', {
          detail: { execution_id: payload.execution_id, item_id: payload.item_id, is_done: true },
        }));
        return true;
      }
      return false;
    case 'add_execution_note':
      if (payload.execution_id && payload.item_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:item', {
          detail: { execution_id: payload.execution_id, item_id: payload.item_id, notes: payload.notes },
        }));
        return true;
      }
      return false;
    case 'set_execution_metric':
      if (payload.execution_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:header', {
          detail: { execution_id: payload.execution_id, field: payload.field, value: payload.value },
        }));
        return true;
      }
      return false;
    case 'complete_execution':
      if (payload.execution_id) {
        window.dispatchEvent(new CustomEvent('vukz:execution:complete', {
          detail: { execution_id: payload.execution_id, ...payload },
        }));
      }
      if (onToast) onToast('Treino concluído com sucesso!', 'success');
      return true;
    default:
      return false;
  }
};

/**
 * Render Markdown-like text as simple HTML segments.
 * Lightweight parser — no external dep. Handles: headers, bold, italic, lists, code, links.
 *
 * @param {string} text — Markdown content
 * @returns {string} HTML string (safe for dangerouslySetInnerHTML)
 */
export const renderMarkdown = (text) => {
  if (!text) return '';

  let html = text
    // Escape HTML entities
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Code blocks (```...```)
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) =>
      `<pre class="assist-code-block"><code>${code.trim()}</code></pre>`
    )
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="assist-inline-code">$1</code>')
    // Headers
    .replace(/^### (.+)$/gm, '<h4 class="assist-h4">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="assist-h3">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="assist-h2">$1</h2>')
    // Bold + Italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered lists
    .replace(/^[-•] (.+)$/gm, '<li class="assist-li">$1</li>')
    // Ordered lists (basic)
    .replace(/^\d+\. (.+)$/gm, '<li class="assist-li-ordered">$1</li>')
    // Links (markdown [text](url))
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="assist-link">$1</a>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="assist-hr" />')
    // Line breaks (double newline → paragraph break, single → <br>)
    .replace(/\n\n/g, '</p><p class="assist-p">')
    .replace(/\n/g, '<br/>');

  // Wrap in paragraph
  html = `<p class="assist-p">${html}</p>`;

  // Clean up empty paragraphs
  html = html.replace(/<p class="assist-p"><\/p>/g, '');

  // Wrap consecutive <li> in <ul>
  html = html.replace(
    /(<li class="assist-li">[\s\S]*?<\/li>)+/g,
    (match) => `<ul class="assist-ul">${match}</ul>`
  );
  html = html.replace(
    /(<li class="assist-li-ordered">[\s\S]*?<\/li>)+/g,
    (match) => `<ol class="assist-ol">${match}</ol>`
  );

  return html;
};

/**
 * Determine response display type for UI rendering.
 * @param {Object} response — { type, content, action, payload }
 * @returns {'chat'|'action'|'assist'|'error'}
 */
export const getResponseType = (response) => {
  if (!response) return 'error';
  if (response.type === 'ASSIST') return 'assist';
  if (response.type === 'ACTION') return 'action';
  if (response.type === 'CHAT') return 'chat';
  return 'chat'; // fallback
};
